/**
 * Centralized Endpoints Registry
 * All API endpoints are defined here for single source of truth
 */

export const ENDPOINTS = {
  // Health and status
  health: '/health',
  
  // Expat questionnaire endpoints
  expat: {
    completeQuestionnaire: '/expat/questionnaire/complete',
    saveQuestionnaire: '/expat/questionnaire/save',
    startQuestionnaire: '/expat/questionnaire/start',
    answerQuestion: '/expat/questionnaire/answer',
  },
  
  // Questionnaire endpoints (new questionnaire system)
  questionnaire: {
    list: '/questionnaire',
    get: '/questionnaire/:id',
    create: '/questionnaire',
    update: '/questionnaire/:id',
    delete: '/questionnaire/:id',
    getQuestions: '/questionnaire/:id/questions',
    // Sessions
    createSession: '/questionnaire/session',
    getSession: '/questionnaire/session/:sessionId',
    updateSession: '/questionnaire/session/:sessionId',
    deleteSession: '/questionnaire/session/:sessionId',
    getSessionSummary: '/questionnaire/session/:sessionId/summary',
    getNextQuestion: '/questionnaire/session/:sessionId/next',
    submitAnswer: '/questionnaire/session/:sessionId/answer',
    completeSession: '/questionnaire/session/:sessionId/complete',
    getSessionAnswers: '/questionnaire/session/:sessionId/answers',
  },
  
  // Tax calculation endpoints
  tax: {
    calculate: '/tax/calculate',
    calculateDetailed: '/tax/calculate-detailed',
    calculateFromQuestionnaire: '/tax/calculate-from-questionnaire',
    quickEstimate: '/tax/quick-estimate',
  },
  
  // Tax rules endpoints
  rules: {
    list: '/rules/rules',
    get: '/rules/rules/:id',
    getByYear: '/rules/rules',
    getByCategory: '/rules/rules',
    summary: '/rules/rules/summary',
    categories: '/rules/rules/categories',
    years: '/rules/rules/years',
  },
  
  // Extraction endpoints
  extraction: {
    uploadFile: '/extract/upload-file',
    extractText: '/extract/extract-text',
    extractAndSave: '/extract/extract-and-save',
    downloadCsv: '/extract/download-csv',
  },
  
  // Validation endpoints
  validation: {
    validateRules: '/validation/rules',
    analyzePatterns: '/validation/analyze-patterns',
  },

  // Calculation records (audit .md files)
  calculationRecords: {
    list: '/calculation-records',
    get: '/calculation-records/:filename',
    download: '/calculation-records/:filename/download',
    delete: '/calculation-records/:filename',
  },
} as const;

/**
 * Path template replacement
 * Replaces :param with actual values
 * 
 * @example
 * path('/users/:id', { id: '123' }) => '/users/123'
 * path('/users/:userId/posts/:postId', { userId: '1', postId: '2' }) => '/users/1/posts/2'
 */
export function path(
  template: string,
  params?: Record<string, string | number>
): string {
  if (!params) return template;
  
  return template.replace(/:(\w+)/g, (match, key) => {
    const value = params[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing required path parameter: ${key}`);
    }
    return String(value);
  });
}

/**
 * Append query parameters to URL
 * Handles arrays, null/undefined values, and encoding
 * 
 * @example
 * withQuery('/api/users', { page: 1, limit: 10 }) => '/api/users?page=1&limit=10'
 * withQuery('/api/users', { tags: ['a', 'b'] }) => '/api/users?tags=a&tags=b'
 * withQuery('/api/users', { search: null }) => '/api/users'
 */
export function withQuery(
  url: string,
  query?: Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>
): string {
  if (!query) return url;
  
  const params = new URLSearchParams();
  
  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }
    
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined) {
          params.append(key, String(item));
        }
      });
    } else {
      params.append(key, String(value));
    }
  });
  
  const queryString = params.toString();
  if (!queryString) return url;
  
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
}

/**
 * Helper to build full endpoint URL
 * Combines path replacement and query parameters
 */
export function buildUrl(
  template: string,
  options?: {
    params?: Record<string, string | number>;
    query?: Record<string, string | number | boolean | (string | number | boolean)[] | null | undefined>;
  }
): string {
  let url = path(template, options?.params);
  url = withQuery(url, options?.query);
  return url;
}


